import React, { useEffect, useState } from "react";
import TextField from '@mui/material/TextField';
import { Autocomplete, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { GridRowsProp, GridColDef, GridFilterModel, DataGridPremium } from '@mui/x-data-grid-premium';
import { Button } from "@mui/joy";
import { Autorenew, HowToReg } from "@mui/icons-material";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import totalLocationData from "../../../../data/locationdata";
import { getFetch, postFetch } from "../../../../fetch/fetch";

// 방화벽 해제 > 주소등록하기 > "선생님" 라디오 선택 시 화면
// 학생 등록(wifipermit.tsx 주소등록하기 탭)과 동일한 UX + 지점 토글 + 선생님 정책(srcaddr) 등록 상태/해제
// 지점별 선생님 정책 ID 는 서버 data.ts fortigateData.teacherPolicyNumber 하드코딩 — 404 면 "정책 ID 확인 필요" 안내만

const API = "/dashboard/wifipermit";

const toastOptions: any = {
    position: "bottom-right",
    autoClose: 2500,
    hideProgressBar: true,
    theme: "colored"
};

const columns2: GridColDef[] = [
    { field: 'ip', headerName: 'IP', width: 150 },
    { field: 'macaddr', headerName: '맥주소', width: 200 },
    { field: "deviceName", headerName: "기기이름", width: 200 },
    { field: "hostname", headerName: "등록된 이름", width: 200 }
];

const errorMessage: any = {
    NOT_LOGIN: "로그인 정보를 확인해주세요",
    NOT_DATA: "요청 정보가 부족합니다",
    NOT_LOCATION: "지점 정보를 확인해주세요",
    NOT_TEACHER: "선생님/직원 계정만 등록할 수 있습니다",
    NO_FORTIGATE: "이 지점은 FortiGate 접속 정보가 없습니다",
    NO_TEACHER_POLICY: "이 지점은 선생님 정책 ID(teacherPolicyNumber)가 설정되지 않았습니다",
    POLICY_NOT_FOUND: "정책 ID 확인 필요 — data.ts teacherPolicyNumber 가 FortiGate 에 없습니다",
    POLICY_ADD_FAIL: "정책 등록 실패 — 생성한 address 는 롤백했습니다",
    HALF_REGISTERED: "address 는 생성됐지만 정책 등록에 실패했습니다. 목록에서 '정책등록 재시도'를 눌러주세요",
    NAME_TAKEN: "같은 이름의 address 가 이미 있습니다. 다시 시도해주세요",
    LAST_SRCADDR: "정책의 마지막 주소라 해제할 수 없습니다. FortiGate 에서 직접 처리해주세요",
    ADDRESS_IN_USE: "다른 정책/그룹에서 참조 중이라 삭제할 수 없습니다",
    NOT_ADDRESS: "해당 address 를 찾을 수 없습니다",
    ADDRESS_LIST_FAIL: "FortiGate address 목록 조회에 실패했습니다",
    FAIL: "처리 실패"
};

const messageOf = (code: string) => errorMessage[code] || `처리 실패 (${code})`;

const WifiPermitTeacher: React.FC<any> = (props) => {

    const branches = totalLocationData.filter((each) => props.user && each.academy.includes(props.user.academy) && each.english !== "total");

    const getInitialLocation = () => {
        if (props.user && props.user.location) {
            const mine = branches.find((each) => each.korean === props.user.location);
            if (mine) return mine.english;
        }
        return branches.length > 0 ? branches[0].english : "";
    }

    const [selectedLocation, setSelectedLocation] = useState<string>(getInitialLocation());
    const [teachers, setTeachers] = useState<any[]>([]);
    const [selectedTeacher, setSelectedTeacher] = useState<any>(null);

    const [deviceList, setDeviceList] = useState<any[]>([]);
    const [policyInfo, setPolicyInfo] = useState<any>({ policyStatus: "", policyId: null, policyName: "" });
    const [deviceLoading, setDeviceLoading] = useState(false);

    const [rows2, setRows2] = useState<GridRowsProp>([]);
    const [loading3, setLoading3] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [filterModel2, setFilterModel2] = useState<GridFilterModel>({
        items: [
            {
                id: 1, field: "macaddr", operator: "contains", value: "",
            }
        ],
    });
    const [selectedMacAddr, setSelectedMacAddr] = useState("");
    const [isRandomizedMacAddress, setIsRandomizedMacAddress] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dhcpUpdate, setDhcpUpdate] = useState(0);

    const selectedBranch = branches.find((each) => each.english === selectedLocation);
    const koreanLocation = selectedBranch ? selectedBranch.korean : "";

    //선생님 목록
    useEffect(() => {

        async function start() {
            try {
                const result: any = await getFetch(`${API}/teacher/users`);

                if (!result || result.message !== "success" || !Array.isArray(result.data)) {
                    return;
                }

                const rows: any[] = [];
                result.data.forEach((each: any) => {
                    rows.push({
                        id: each.id,
                        name: each.name,
                        location: each.location,
                        label: `${each.name} <${each.location ? each.location : "미지정"}>`
                    });
                });
                setTeachers([...rows]);
            } catch (e) {
                console.log(e);
            }
        }

        start();

    }, []);

    //지점 변경 → DHCP 리스트 재조회
    useEffect(() => {

        if (!koreanLocation) {
            return;
        }

        setSelectedMacAddr("");
        setRows2([]);
        loadDhcp(koreanLocation);

    }, [koreanLocation, dhcpUpdate]);

    //선생님/지점 변경 → 등록된 기기 목록
    useEffect(() => {

        setSelectedMacAddr("");

        if (!selectedTeacher || !koreanLocation) {
            setDeviceList([]);
            setPolicyInfo({ policyStatus: "", policyId: null, policyName: "" });
            return;
        }

        loadDevices();

    }, [selectedTeacher, koreanLocation]);

    //macaddr의 두번째 글자가 2, 6, A, E 일 경우 랜덤화된 맥주소라고 판단 (학생 등록과 동일)
    useEffect(() => {

        if (!selectedMacAddr) {
            setIsRandomizedMacAddress(false);
            return;
        }

        if (selectedMacAddr[1] === "2" || selectedMacAddr[1] === "6" || selectedMacAddr[1] === "a" || selectedMacAddr[1] === "e") {
            setIsRandomizedMacAddress(true);
        } else {
            setIsRandomizedMacAddress(false);
        }

    }, [selectedMacAddr]);

    const onTeacherChange = (e: any, value: any) => {

        setSelectedTeacher(value ? value : null);

        if (!value) {
            return;
        }

        //선생님 소속 지점이 토글 목록에 있으면 자동 선택 (운영진/미지정은 그대로)
        const mine = branches.find((each) => each.korean === value.location);

        if (mine) {
            setSelectedLocation(mine.english);
        }
    }

    const loadDhcp = async (location: string) => {

        if (!location) {
            return;
        }

        setLoading3(true);

        try {

            const result: any = await getFetch(`${API}/getTotalDhcpList?location=${encodeURIComponent(location)}`);

            if (!result || result.message !== "success" || !Array.isArray(result.totalDhcpList) || !Array.isArray(result.totalAddressList)) {
                setLoading3(false);
                return;
            }

            const totalDhcpList = result.totalDhcpList;
            const totalAddressList = result.totalAddressList;

            //이미 등록된 맥주소면 등록된 이름 표시 (학생 등록과 동일 로직)
            totalDhcpList.forEach((each: any) => {
                totalAddressList.forEach((each2: any) => {

                    if (!each2.macaddr) {
                        return;
                    }

                    if (each2.macaddr.length === 0) {
                        return;
                    }

                    if (!each2.macaddr[0].macaddr) {
                        return;
                    }

                    const eachMacaddr = each2.macaddr[0].macaddr;

                    //FortiGate 는 MAC 을 대문자로 돌려주기도 함 → 대소문자 무시 비교
                    if (String(each.mac).toLowerCase() === String(eachMacaddr).toLowerCase()) {
                        each.name = each2.name;
                    }
                })
            });

            const newRows: any = [];

            totalDhcpList.forEach((each: any, index: number) => {

                const oneRow: any = {};

                oneRow.id = index;
                oneRow.ip = each.ip;
                oneRow.macaddr = each.mac;
                oneRow.deviceName = each.hostname;
                oneRow.hostname = each.name;

                newRows.push(oneRow);

            });

            setRows2([...newRows]);

        } catch (e) {
            console.log(e);
            toast.error("접속 기기 목록 조회 실패", toastOptions);
        }

        setLoading3(false);
    }

    const loadDevices = async () => {

        if (!selectedTeacher || !koreanLocation) {
            return;
        }

        setDeviceLoading(true);

        try {

            const result: any = await getFetch(`${API}/teacher/devices?userId=${selectedTeacher.id}&location=${encodeURIComponent(koreanLocation)}`);

            if (!result || result.message !== "success") {
                setDeviceList([]);
                setPolicyInfo({ policyStatus: result && result.message ? result.message : "FAIL", policyId: null, policyName: "" });
                setDeviceLoading(false);
                return;
            }

            setDeviceList(Array.isArray(result.deviceList) ? [...result.deviceList] : []);
            setPolicyInfo({
                policyStatus: result.policyStatus,
                policyId: result.policyId,
                policyName: result.policyName
            });

        } catch (e) {
            console.log(e);
            setPolicyInfo({ policyStatus: "FAIL", policyId: null, policyName: "" });
        }

        setDeviceLoading(false);
    }

    const letsUpdate2 = () => {
        setDhcpUpdate(Math.random());
    }

    const handleSearchTextChange = (e: any) => {

        setSearchText(e.target.value);

        const newFilterModel = filterModel2;

        newFilterModel.items.forEach((each: any) => {
            if (each.id === 1) {
                each.value = e.target.value;
            }
        })

        setFilterModel2({ ...newFilterModel });
    }

    const submitAddress = async () => {

        if (!selectedTeacher || !selectedMacAddr || !koreanLocation) {
            return;
        }

        if (!window.confirm(`${selectedTeacher.name} 선생님 기기로 저장하시겠습니까?\n(${selectedMacAddr})`)) {
            return;
        }

        setSaving(true);

        try {

            const result: any = await postFetch(`${API}/teacher/postAddress`, {
                userId: selectedTeacher.id,
                location: koreanLocation,
                macaddr: selectedMacAddr
            });

            if (result && result.message === "success") {
                toast.success(`address 생성 + ${result.policyName ? result.policyName : "선생님"} 정책 등록 완료`, toastOptions);
                setSelectedMacAddr("");
                loadDevices();
                setDhcpUpdate(Math.random());
            } else {
                const code = result && result.message ? result.message : "FAIL";
                toast.error(messageOf(code), { ...toastOptions, autoClose: 5000 });

                if (code === "HALF_REGISTERED" || code === "POLICY_NOT_FOUND") {
                    loadDevices();
                    setDhcpUpdate(Math.random());
                }
            }

        } catch (e) {
            console.log(e);
            toast.error("서버 응답 없음", toastOptions);
        }

        setSaving(false);
    }

    const deleteDevice = async (name: string) => {

        if (!selectedTeacher || !koreanLocation) {
            return;
        }

        if (!window.confirm(`${name} 기기를 해제하시겠습니까?\n(정책 srcaddr 제거 후 address 삭제)`)) {
            return;
        }

        setSaving(true);

        try {

            const result: any = await postFetch(`${API}/teacher/deleteAddress`, {
                userId: selectedTeacher.id,
                location: koreanLocation,
                name
            });

            if (result && result.message === "success") {
                toast.success("기기 해제 완료", toastOptions);
            } else {
                const code = result && result.message ? result.message : "FAIL";
                toast.error(messageOf(code), { ...toastOptions, autoClose: 5000 });
            }

        } catch (e) {
            console.log(e);
            toast.error("서버 응답 없음", toastOptions);
        }

        loadDevices();
        setDhcpUpdate(Math.random());
        setSaving(false);
    }

    const retryPolicy = async (name: string) => {

        if (!selectedTeacher || !koreanLocation) {
            return;
        }

        setSaving(true);

        try {

            const result: any = await postFetch(`${API}/teacher/retryPolicy`, {
                userId: selectedTeacher.id,
                location: koreanLocation,
                name
            });

            if (result && result.message === "success") {
                toast.success(`${result.policyName ? result.policyName : "선생님"} 정책 등록 완료`, toastOptions);
            } else {
                const code = result && result.message ? result.message : "FAIL";
                toast.error(messageOf(code), { ...toastOptions, autoClose: 5000 });
            }

        } catch (e) {
            console.log(e);
            toast.error("서버 응답 없음", toastOptions);
        }

        loadDevices();
        setSaving(false);
    }

    const disabled = !selectedMacAddr || !selectedTeacher || policyInfo.policyStatus !== "ok" || saving;

    const renderPolicyBadge = (each: any) => {

        if (each.inPolicy === true) {
            return <span style={{ color: "#1976d2", marginLeft: "6px", fontSize: "13px" }}>정책등록됨</span>;
        }

        if (each.inPolicy === false) {
            return (
                <span style={{ marginLeft: "6px", fontSize: "13px" }}>
                    <span style={{ color: "#d32f2f" }}>정책 미등록</span>
                    <Button size="sm" variant="outlined" sx={{ marginLeft: "6px" }} disabled={saving} onClick={() => { retryPolicy(each.name); }}>
                        정책등록 재시도
                    </Button>
                </span>
            );
        }

        return <span style={{ color: "gray", marginLeft: "6px", fontSize: "13px" }}>정책 확인 필요</span>;
    }

    const renderPolicyStatus = () => {

        if (!selectedTeacher) {
            return null;
        }

        if (policyInfo.policyStatus === "ok") {
            return <span style={{ fontSize: "14px", color: "gray", fontFamily: "Apple_R" }}>정책 : {policyInfo.policyName} (#{policyInfo.policyId})</span>;
        }

        if (!policyInfo.policyStatus) {
            return null;
        }

        return <span style={{ fontSize: "14px", color: "#d32f2f", fontFamily: "Apple_R" }}>{policyInfo.policyStatus === "notFound" ? messageOf("POLICY_NOT_FOUND") : messageOf(policyInfo.policyStatus)}</span>;
    }

    return (
        <div>
            <div style={{
                fontSize: "14px",
                marginBottom: "12px",
                color: "gray"
            }}>
                ** 선생님 기기에서 랜덤 맥주소를 끄고 기기 맥주소를 사용하도록 설정해주세요. 등록 시 address 생성 후 지점 선생님 허용 정책에 자동 추가됩니다.
            </div>

            <div style={{
                marginBottom: "32px"
            }}>
                <div style={{
                    fontSize: "20px",
                    fontFamily: "Apple_SB",
                    marginBottom: "18px"
                }}>
                    - 지점 선택
                </div>
                <ToggleButtonGroup
                    color="primary"
                    value={selectedLocation}
                    exclusive
                    onChange={(e: any, newValue: any) => {
                        if (newValue) {
                            setSelectedLocation(newValue);
                        }
                    }}
                >
                    {
                        branches.map((each, index) => {
                            return (
                                <ToggleButton key={index} value={each.english}>{each.korean}</ToggleButton>
                            );
                        })
                    }
                </ToggleButtonGroup>
            </div>

            <div style={{
                marginBottom: "32px"
            }}>
                <div style={{
                    fontSize: "20px",
                    fontFamily: "Apple_SB",
                    marginBottom: "18px"
                }}>
                    - 선생님 선택
                </div>
                <div style={{
                    display: "flex",
                }}>
                    <Autocomplete
                        onChange={onTeacherChange}
                        value={selectedTeacher}
                        disablePortal
                        id="teacher-combo-box"
                        options={teachers}
                        isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                        sx={{ width: "250px", marginRight: "12px" }}
                        renderInput={(params) => <TextField {...params} placeholder="선생님을 선택하세요" />}
                    />
                </div>
            </div>

            <div style={{
                marginBottom: "32px"
            }}>
                <div style={{
                    fontSize: "20px",
                    fontFamily: "Apple_SB",
                    marginBottom: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "1200px"
                }}>
                    <span>
                        - 선택된 선생님의 현재 등록된 기기 리스트
                        {
                            selectedTeacher &&
                            <span style={{
                                marginLeft: "12px",
                                paddingTop: "4px",
                                cursor: "pointer"
                            }}
                                onClick={loadDevices}
                            >
                                <Autorenew fontSize="small" />
                            </span>
                        }
                    </span>
                    {renderPolicyStatus()}
                </div>
                <div style={{
                    fontSize: "13px",
                    color: "gray",
                    marginBottom: "8px"
                }}>
                    (FortiGate 에서 수동 등록한 이름_ID 형식의 address 도 함께 표시됩니다)
                </div>
                <div style={{
                    minHeight: "30px",
                }}>
                    {
                        deviceLoading &&
                        <div>불러오는 중...</div>
                    }
                    {
                        !deviceLoading && deviceList.map((each: any, index: number) => {
                            return (
                                <div key={index} style={{
                                    display: "flex",
                                    alignItems: "center",
                                    marginBottom: "8px"
                                }}>
                                    <span>({index + 1}) {each.name} ({each.macaddr})</span>
                                    {renderPolicyBadge(each)}
                                    <Button size="sm" color="danger" variant="outlined" sx={{ marginLeft: "12px" }} disabled={saving} onClick={() => { deleteDevice(each.name); }}>
                                        해제
                                    </Button>
                                </div>
                            )
                        })
                    }
                    {
                        !deviceLoading && deviceList.length === 0 &&
                        <div>
                            -
                        </div>
                    }
                </div>
            </div>

            {
                koreanLocation &&
                <div style={{
                    marginBottom: "32px",
                    width: "1200px"
                }}>
                    <div style={{
                        fontSize: "20px",
                        fontFamily: "Apple_SB",
                        marginBottom: "18px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between"
                    }}>
                        <span>
                            - {koreanLocation}에 접속된 기기 리스트
                            <span style={{
                                marginLeft: "12px",
                                paddingTop: "4px",
                                cursor: "pointer"
                            }}
                                onClick={letsUpdate2}
                            >
                                <Autorenew fontSize="small" />
                            </span>
                        </span>
                        <div>
                            <TextField sx={{ fontSize: "24px", marginLeft: "12px" }} label="맥주소 검색" variant="standard" value={searchText} onChange={handleSearchTextChange} />
                        </div>
                    </div>
                    <div style={{ flexGrow: 1, height: 500, backgroundColor: "white" }}>
                        <DataGridPremium
                            rowHeight={40}
                            loading={loading3}
                            rows={rows2}
                            columns={columns2}
                            sx={{
                                "& .notPermit": {
                                    color: "rgb(205, 205, 198)"
                                }
                            }}
                            isRowSelectable={(params) => {
                                if (params.row.hostname) {
                                    return false;
                                }
                                return true;
                            }}
                            filterModel={filterModel2}
                            onFilterModelChange={(newModel) => { setFilterModel2(newModel); }}
                            getCellClassName={(params) => {
                                if (params.row.hostname) {
                                    return "notPermit"
                                }
                                return ""
                            }}
                            onRowClick={(params) => {

                                if (params.row.hostname) {
                                    return;
                                }

                                setSelectedMacAddr(params.row.macaddr);
                            }}
                        />
                    </div>
                    <div style={{
                        marginTop: "24px",
                        display: "flex",
                        justifyContent: "flex-end",
                        minHeight: "50px"
                    }}>
                        <div>
                            <span style={{
                                color: isRandomizedMacAddress ? "red" : "black"
                            }}>
                                <div style={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                }}>선택된 맥 주소 : {selectedMacAddr}</div>
                                <div>{isRandomizedMacAddress ? "(랜덤화된 맥주소일 확률이 높습니다. 선생님 기기를 검사해주세요)" : ""}</div>
                            </span>
                        </div>
                    </div>
                    <div style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center"
                    }}>
                        {
                            selectedTeacher && policyInfo.policyStatus && policyInfo.policyStatus !== "ok" &&
                            <span style={{ color: "#d32f2f", fontSize: "13px", marginRight: "12px" }}>
                                정책 상태를 확인해야 등록할 수 있습니다
                            </span>
                        }
                        {
                            !selectedTeacher &&
                            <span style={{ color: "gray", fontSize: "13px", marginRight: "12px" }}>
                                선생님을 먼저 선택하세요
                            </span>
                        }
                        <Button startDecorator={<HowToReg />} disabled={disabled} loading={saving}
                            onClick={submitAddress}
                        >
                            선생님 기기로 저장
                        </Button>
                    </div>
                </div>
            }
        </div>
    )
}

export default WifiPermitTeacher;
